import { formatIstDate, formatIstDateTime } from '../../../utils/helpers/istDate';
// Shared vocabulary for the Visitor Management (VMS) module.
//
// A "visitor" here is the person, not the visit: one master row per
// (society_id, phone) pair, with visits, pre-approvals and gate passes hanging
// off it. That is why the screens talk about registering someone once and then
// blocking or editing that record, rather than logging entries.
//
// These maps mirror the backend enums. Tailwind's JIT only sees class names it
// can read literally, so every style variant is spelled out here rather than
// assembled from fragments at render time.

export const VISITOR_TYPES = [
    'GUEST', 'DELIVERY', 'CAB', 'VENDOR',
    'STAFF', 'DOMESTIC_HELP', 'AGENT', 'OTHER',
];

export const ID_PROOF_TYPES = [
    'AADHAR', 'PAN', 'VOTER_ID', 'DRIVING_LICENSE', 'PASSPORT', 'OTHER',
];

export const VISITOR_TYPE_STYLE = {
    GUEST: 'tw-bg-sky-100 tw-text-sky-700',
    DELIVERY: 'tw-bg-amber-100 tw-text-amber-700',
    CAB: 'tw-bg-yellow-100 tw-text-yellow-800',
    VENDOR: 'tw-bg-violet-100 tw-text-violet-700',
    STAFF: 'tw-bg-emerald-100 tw-text-emerald-700',
    DOMESTIC_HELP: 'tw-bg-teal-100 tw-text-teal-700',
    AGENT: 'tw-bg-fuchsia-100 tw-text-fuchsia-700',
    OTHER: 'tw-bg-slate-100 tw-text-slate-700',
};

// Soft tint used for the avatar tile so a list of visitors reads by type at a
// glance even before the badge is parsed.
export const VISITOR_TYPE_AVATAR = {
    GUEST: 'tw-bg-sky-50 tw-text-sky-600',
    DELIVERY: 'tw-bg-amber-50 tw-text-amber-600',
    CAB: 'tw-bg-yellow-50 tw-text-yellow-700',
    VENDOR: 'tw-bg-violet-50 tw-text-violet-600',
    STAFF: 'tw-bg-emerald-50 tw-text-emerald-600',
    DOMESTIC_HELP: 'tw-bg-teal-50 tw-text-teal-600',
    AGENT: 'tw-bg-fuchsia-50 tw-text-fuchsia-600',
    OTHER: 'tw-bg-slate-100 tw-text-slate-600',
};

export const VISITOR_TYPE_ICON = {
    GUEST: 'mdi:account-outline',
    DELIVERY: 'mdi:package-variant-closed',
    CAB: 'mdi:taxi',
    VENDOR: 'mdi:briefcase-outline',
    STAFF: 'mdi:account-hard-hat-outline',
    DOMESTIC_HELP: 'mdi:broom',
    AGENT: 'mdi:home-search-outline',
    OTHER: 'mdi:dots-horizontal-circle-outline',
};

export const ID_PROOF_LABEL = {
    AADHAR: 'Aadhaar',
    PAN: 'PAN',
    VOTER_ID: 'Voter ID',
    DRIVING_LICENSE: 'Driving Licence',
    PASSPORT: 'Passport',
    OTHER: 'Other',
};

export const ID_PROOF_ICON = {
    AADHAR: 'mdi:card-account-details-outline',
    PAN: 'mdi:card-text-outline',
    VOTER_ID: 'mdi:vote-outline',
    DRIVING_LICENSE: 'mdi:card-account-details-star-outline',
    PASSPORT: 'mdi:passport',
    OTHER: 'mdi:file-document-outline',
};

// Field limits enforced by the API's Joi schema. Kept here so the inputs cap
// the user before a round-trip turns it into a 400.
export const LIMITS = {
    name: 150,
    phone: 20,
    email: 100,
    company_name: 150,
    id_proof_number: 50,
    photo_url: 500,
    address: 500,
    notes: 1000,
    blocked_reason: 255,
};

export const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const typeLabel = (t) => (t === 'DOMESTIC_HELP' ? 'Domestic Help' : humanize(t));

export const fmtDate = (d) =>
    d ? formatIstDate(d) : '—';

export const fmtDateTime = (d) =>
    d ? formatIstDateTime(d) : '—';

// is_blocked / is_deleted come back as 0|1 integers, not booleans. Both are
// falsy-safe for 0, but a strict `=== false` would never match — hence a
// helper rather than raw comparisons scattered through the screens.
export const isBlocked = (v) => Number(v?.is_blocked) === 1;

// The phone is stored verbatim and uniqueness is per (society, phone), so
// "9876543210" and "+919876543210" would become two separate visitors. Collapse
// the usual Indian spellings onto the bare 10-digit subscriber number and leave
// genuine international numbers in +E.164 form.
export const normalizePhone = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
    if (hasPlus && digits.length > 10) return `+${digits}`;
    return digits;
};

// Display form: 98765 43210 reads far faster than 9876543210 in a table.
export const fmtPhone = (raw) => {
    const p = (raw || '').trim();
    if (/^\d{10}$/.test(p)) return `${p.slice(0, 5)} ${p.slice(5)}`;
    return p || '—';
};

export const isValidPhone = (raw) => {
    const p = normalizePhone(raw);
    // 10 local digits, or an international number of 8–15 digits after the +.
    return /^\d{10}$/.test(p) || /^\+\d{8,15}$/.test(p);
};

export const isValidEmail = (raw) =>
    !raw || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());

// ID numbers are personal data; the list shows only the tail so a shoulder
// surfer can't lift a full Aadhaar off a shared screen. The detail view can
// reveal the whole thing on request.
export const maskIdNumber = (num) => {
    const s = (num || '').trim();
    if (!s) return '';
    const visible = s.slice(-4);
    return `${'•'.repeat(Math.max(s.length - 4, 2))}${visible}`;
};

export const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
